/**
 * useMe — TanStack Query hook that fetches the authenticated user's profile.
 *
 * Endpoint: GET /api/v1/me
 * Response: MeResponse (id, telegramId, firstName, languageCode, onboardingStep,
 *           createdAt, birthDate, birthTimeExact, birthTimePrecision, birthPlaceName)
 *
 * JWT-gated: query is disabled when no JWT is present (enabled: false).
 * Auth gate upstream should ensure the user is authenticated before Me is rendered.
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';

export interface NotificationPrefs {
  master: boolean;
  purchases: boolean;
  partnerActivity: boolean;
  dailyAdvice: boolean;
  promo: boolean;
  dailyAdviceHour: number;
  dailyAdviceTone: 'gentle' | 'direct' | 'poetic';
}

export interface PrivacyPrefs {
  selfieStorageEnabled: boolean;
  namePublic: boolean;
  avatarPublic: boolean;
}

export type LanguageCode = 'en' | 'ru' | 'uk';

export interface MeResponse {
  id: string;
  telegramId: string;
  firstName: string | null;
  sex: 'MALE' | 'FEMALE' | null;
  language: LanguageCode;
  languageCode: LanguageCode;
  onboardingStep: number;
  createdAt: string;
  birthDate: string | null;
  birthTimeExact: string | null;
  birthTimePrecision: string | null;
  birthPlaceName: string | null;
  birthLat: number | null;
  birthLng: number | null;
  birthTimezone: string | null;
  ageConfirmedAt: string | null;
  gdprConsentAt: string | null;
  consentSelfieAt: string | null;
  consentSelfieVersion: string | null;
  selfieStarPersonaGenerated: boolean;
  genericStarPersonaGenerated: boolean;
  profileAvatarR2Key: string | null;
  profileAvatarUrl: string | null;
  founderNumber: number | null;
  notificationPrefs: NotificationPrefs;
  privacyPrefs: PrivacyPrefs;
  publicProfileEnabled: boolean;
  /** Runtime feature flag for the past-lives freemium ladder (server-driven). */
  pastLivesFreemiumEnabled?: boolean;
  /** Image generation + Purchases section switch (server-driven, default off). */
  imageFeaturesEnabled?: boolean;
}

export function useMe(): {
  data: MeResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const jwt = useAuth((s) => s.jwt);

  const query = useQuery<MeResponse, Error>({
    queryKey: ['me'],
    queryFn: async () => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await apiFetch('/api/v1/me', jwt);
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`me_fetch_failed: ${res.status} ${body}`);
      }
      return (await res.json()) as MeResponse;
    },
    enabled: !!jwt,
    staleTime: 30_000,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
